import { Stack, Typography } from "@mui/material";

import { orange, state as stateColor } from "@/theme/colors";
import { rounded } from "@/theme/variables";
import { CreateTalentFromCardTabForm } from "@/features/talent/components/talent-scan-image/CreateTalentFromCardTabForm";
import { CreateTalentFromMyNumberCardTabForm } from "@/features/talent/components/talent-scan-image/CreateTalentFromMyNumberCardTabForm";
import { CreateTalentFromResumeTabForm } from "@/features/talent/components/talent-scan-image/CreateTalentFromResumeTabForm";
import { CreateTalentFromAllForm } from "@/features/talent/components/talent-scan-image/CreateTalentFromAllForm";
import { UseScanImageStateReturn } from "@/features/talent/components/talent-scan-image/useScanImageState";
import { TMyNumberCardResDto } from "@/api/services/main/talent/dtos/generate-scan-image.dto";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { dayjs } from "@/utils/dayjs";
import { GENDER } from "@/types/enum";

type Props = {
  state: UseScanImageStateReturn;
};

function mapMyNumberCardToForm(bucket: TMyNumberCardResDto): TTalentForm {
  const combinedAddress = [bucket.prefecture, bucket.city, bucket.address].filter(Boolean).join("");
  return {
    fullName: bucket.fullName ?? null,
    gender: bucket.gender === GENDER.OTHER ? undefined : bucket.gender,
    birthday: bucket.birthday ? dayjs(bucket.birthday) : null,
    zipCode: bucket.zipCode ?? null,
    prefecture: bucket.prefecture ?? null,
    city: bucket.city ?? null,
    address: bucket.address ?? null,
    fullResidenceAddress: combinedAddress || null,
    cardNumber: bucket.cardNumber ?? null,
    nationalityId: bucket.nationalityId ?? undefined,
    residenceStatusId: bucket.residenceStatusId ?? null,
    periodOfStay: bucket.periodOfStay ? dayjs(bucket.periodOfStay) : null,
  } as TTalentForm;
}

/**
 * Result panel for the unified scan modal (IROHANA_MATCH_DEV-280).
 * - 全項目 tab: prefilled with mergedTalentForm; uses the resume form because it
 *   carries the richest field set. Card-only fields (cardNumber, cardSide,
 *   backAddresses) ride along on defaultTalentValue and are wired by the form.
 * - 履歴書項目 tab: same resume form, no overlap warning since data is resume-only.
 * - 在留カード項目 tab: dedicated card form for residence-card fields.
 *
 * Phase 5 may unify the two forms; for now this keeps the existing forms intact.
 */
export function ScanResultPanel({ state }: Props) {
  const { activeTab, defaultTalentValue, scanResult, hasScannedBefore, isShowDropZoneUpdateFile, memoWatchField, onGetValuesCreateTalent, onSetValidationTrigger } = state;
  const myNumberCardValue = scanResult?.myNumberCard ? mapMyNumberCardToForm(scanResult.myNumberCard) : defaultTalentValue;
  return (
    <Stack>
      {isShowDropZoneUpdateFile && (
        <Stack
          sx={{
            p: "8px 16px 8px 16px",
            background: stateColor.incomplete_secondary,
            color: orange[80],
            minHeight: "34px",
            marginBottom: "16px",
          }}
          borderRadius={rounded.xs}
        >
          <Typography component="p" variant="cap12">
            AIによる読み取り結果は青字で示されます。抽出ミスがないか確認して、必要に応じて修正ください。
            {hasScannedBefore && (
              <>
                <br />
                2回目以降のAI取り込みです、現在書かれている値と比較して読み取りが正しいか確認してください。
              </>
            )}
          </Typography>
        </Stack>
      )}

      {activeTab === "myNumberCard" ? (
        <CreateTalentFromMyNumberCardTabForm
          defaultValues={myNumberCardValue}
          memo={memoWatchField}
          onChange={onGetValuesCreateTalent}
          onValidate={onSetValidationTrigger}
        />
      ) : activeTab === "card" ? (
        <CreateTalentFromCardTabForm
          defaultValues={defaultTalentValue}
          memo={memoWatchField}
          onChange={onGetValuesCreateTalent}
          onValidate={onSetValidationTrigger}
        />
      ) : activeTab === "all" ? (
        <CreateTalentFromAllForm
          defaultValues={defaultTalentValue}
          memo={memoWatchField}
          onValidate={onSetValidationTrigger}
          onChange={onGetValuesCreateTalent}
        />
      ) : (
        <CreateTalentFromResumeTabForm
          defaultValues={defaultTalentValue}
          memo={memoWatchField}
          onValidate={onSetValidationTrigger}
          onChange={onGetValuesCreateTalent}
        />
      )}
    </Stack>
  );
}
